/**
 * Per-topic raw data sets students can download as CSV and analyze.
 * Generated procedurally so we can ship them as plain TS without binary assets.
 */
export interface TopicDataset {
  filename: string;
  title: string;
  description: string;
  task: string;
  /** First row is the header. */
  rows: (string | number)[][];
}

function round(n: number, d = 2) {
  const f = Math.pow(10, d);
  return Math.round(n * f) / f;
}

// --- Titration: 25 mL of 0.10 M HCl titrated with 0.10 M NaOH ----------------
function buildTitration(): TopicDataset {
  const Ca = 0.1;
  const Cb = 0.1;
  const Va = 25;
  const rows: (string | number)[][] = [["volume_NaOH_mL", "pH"]];
  for (let v = 0; v <= 50; v += 1) {
    const molesA = (Ca * Va) / 1000;
    const molesB = (Cb * v) / 1000;
    const totalL = (Va + v) / 1000;
    const net = molesA - molesB;
    let pH: number;
    if (Math.abs(net) < 1e-9) pH = 7;
    else if (net > 0) pH = -Math.log10(Math.max(net / totalL, 1e-14));
    else pH = 14 - -Math.log10(Math.max(-net / totalL, 1e-14));
    rows.push([v, round(pH, 3)]);
  }
  return {
    filename: "titration_HCl_NaOH.csv",
    title: "Titration: 25 mL of 0.10 M HCl with 0.10 M NaOH",
    description:
      "51 data points covering 0–50 mL of titrant added, with computed pH at each step.",
    task: "Plot pH vs. volume. Identify the equivalence point. Calculate the molarity of the unknown if you only knew the volume at equivalence.",
    rows,
  };
}

// --- Calorimetry: hot copper into water -------------------------------------
function buildCalorimetry(): TopicDataset {
  // 50 g Cu at 95 °C dropped into 100 g water at 22 °C. Newton-cooling-style approach to T_eq.
  const cCu = 0.385;
  const cW = 4.184;
  const mCu = 50;
  const mW = 100;
  const T_Cu0 = 95;
  const T_W0 = 22;
  const T_eq =
    (cCu * mCu * T_Cu0 + cW * mW * T_W0) / (cCu * mCu + cW * mW);
  const k = 0.18; // approach rate
  const rows: (string | number)[][] = [["time_s", "T_water_C", "T_copper_C"]];
  for (let t = 0; t <= 60; t += 2) {
    const Tw = T_eq + (T_W0 - T_eq) * Math.exp(-k * t);
    const Tc = T_eq + (T_Cu0 - T_eq) * Math.exp(-k * t);
    rows.push([t, round(Tw, 2), round(Tc, 2)]);
  }
  return {
    filename: "calorimetry_copper_in_water.csv",
    title: "Calorimetry: 50 g Cu (95 °C) → 100 g H₂O (22 °C)",
    description:
      "Temperature of water and copper logged every 2 seconds as they reach thermal equilibrium.",
    task: "Plot both curves on one axis. Estimate T_equilibrium. Use q = mcΔT to verify q_lost(Cu) ≈ q_gained(water).",
    rows,
  };
}

// --- Gas Laws: pressure-volume of a fixed-mole sample at constant T ---------
function buildGasLaws(): TopicDataset {
  const n = 1.0;
  const R = 0.0821;
  const T = 298.15;
  const rows: (string | number)[][] = [["volume_L", "pressure_atm"]];
  for (let V = 5; V <= 50; V += 2.5) {
    const P = (n * R * T) / V;
    // small simulated noise so students experience real-data scatter
    const noisy = P * (1 + (Math.sin(V * 7.3) * 0.012));
    rows.push([V, round(noisy, 3)]);
  }
  return {
    filename: "boyles_law_PV_data.csv",
    title: "Gas Laws: Pressure–Volume at 298 K (1.00 mol)",
    description:
      "P measured at 19 different volumes, isothermal. Includes small experimental noise.",
    task: "Plot P vs. V (curve) AND P vs. 1/V (line). Use the slope of the linear plot to calculate nRT and back-solve T.",
    rows,
  };
}

// --- Solutions: solubility of KNO₃ vs. temperature --------------------------
function buildSolubility(): TopicDataset {
  // Approximate published solubility of KNO₃ in g per 100 g H₂O.
  const data: [number, number][] = [
    [0, 13.3], [10, 21.2], [20, 31.6], [30, 45.3], [40, 63.9],
    [50, 85.5], [60, 110], [70, 138], [80, 169], [90, 202], [100, 247],
  ];
  const rows: (string | number)[][] = [["temperature_C", "solubility_g_per_100g_water"]];
  data.forEach(([t, s]) => rows.push([t, s]));
  return {
    filename: "solubility_KNO3_vs_temperature.csv",
    title: "Solubility of KNO₃ in water (0–100 °C)",
    description:
      "Tabulated solubility values from CRC Handbook. Smooth, single-curve dataset.",
    task:
      "Plot solubility vs. temperature. Predict how much KNO₃ will crystallize when 100 g of saturated solution at 80 °C is cooled to 20 °C.",
    rows,
  };
}

// --- Half-life decay ---------------------------------------------------------
function buildHalfLife(): TopicDataset {
  // I-131, t½ = 8.02 days. 100 mg initial.
  const T = 8.02;
  const N0 = 100;
  const rows: (string | number)[][] = [["time_days", "mass_mg"]];
  for (let t = 0; t <= 40; t += 2) {
    const m = N0 * Math.pow(0.5, t / T);
    rows.push([t, round(m, 3)]);
  }
  return {
    filename: "iodine131_decay.csv",
    title: "I-131 decay (initial mass 100 mg, t½ = 8.02 days)",
    description: "Mass remaining sampled every 2 days for 40 days.",
    task:
      "Plot mass vs. time, then ln(mass) vs. time. From the slope of the linear plot, recover the half-life and compare to the published value.",
    rows,
  };
}

export const TOPIC_DATASETS: Record<string, TopicDataset> = {
  "lab-foundations": {
    filename: "measurement_repeated_trials.csv",
    title: "Repeated measurement of a 25.00 mL pipette",
    description: "Twelve trials of dispensing what should be 25.00 mL of water.",
    task:
      "Compute mean, standard deviation, and percent error vs. 25.00 mL. Discuss accuracy vs. precision.",
    rows: [
      ["trial", "volume_mL"],
      [1, 24.92], [2, 25.04], [3, 24.98], [4, 25.01], [5, 24.95],
      [6, 25.06], [7, 24.99], [8, 25.02], [9, 24.97], [10, 25.03],
      [11, 24.96], [12, 25.00],
    ],
  },
  "periodic-table": {
    filename: "atomic_radius_period3.csv",
    title: "Atomic radius across Period 3",
    description: "Approximate covalent radii (pm) for Na through Ar.",
    task: "Plot radius vs. atomic number. Explain the trend in terms of effective nuclear charge.",
    rows: [
      ["element", "atomic_number", "radius_pm"],
      ["Na", 11, 186], ["Mg", 12, 160], ["Al", 13, 143], ["Si", 14, 117],
      ["P", 15, 110], ["S", 16, 104], ["Cl", 17, 99], ["Ar", 18, 71],
    ],
  },
  "atomic-structure": {
    filename: "hydrogen_emission_balmer.csv",
    title: "Hydrogen Balmer emission lines",
    description: "Observed wavelengths of the H Balmer series in nm.",
    task:
      "Use the Rydberg formula 1/λ = R(1/2² – 1/n²) to back-calculate R. Identify each transition (n → 2).",
    rows: [
      ["line", "n_initial", "wavelength_nm"],
      ["Hα (red)", 3, 656.3],
      ["Hβ (cyan)", 4, 486.1],
      ["Hγ (blue)", 5, 434.0],
      ["Hδ (violet)", 6, 410.2],
    ],
  },
  bonding: {
    filename: "boiling_points_imf.csv",
    title: "Boiling points: H₂O, H₂S, H₂Se, H₂Te",
    description: "Group 16 hydrides - boiling points (°C).",
    task:
      "Plot bp vs. molar mass. Explain why H₂O is the dramatic outlier. Predict bp of H₂Po qualitatively.",
    rows: [
      ["compound", "molar_mass_g_mol", "boiling_point_C"],
      ["H2O", 18.02, 100],
      ["H2S", 34.08, -60],
      ["H2Se", 80.98, -41],
      ["H2Te", 129.62, -2],
    ],
  },
  stoichiometry: {
    filename: "limiting_reactant_lab.csv",
    title: "Limiting reactant: NaHCO₃ + CH₃COOH → CO₂ + H₂O + NaC₂H₃O₂",
    description: "Five trials varying the mass of baking soda. Vinegar held at 50.0 mL of 5.0% acetic acid.",
    task:
      "Plot CO₂ produced (g) vs. NaHCO₃ added (g). Identify the trial where the limiting reactant switches from NaHCO₃ to acetic acid.",
    rows: [
      ["trial", "NaHCO3_g", "CO2_produced_g"],
      [1, 1.0, 0.52],
      [2, 2.0, 1.04],
      [3, 3.0, 1.55],
      [4, 4.0, 1.78],
      [5, 5.0, 1.81],
    ],
  },
  "gas-laws": buildGasLaws(),
  solutions: buildSolubility(),
  "acid-base": buildTitration(),
  thermochemistry: buildCalorimetry(),
  nuclear: buildHalfLife(),
};

/** Convert a dataset into a CSV string with proper escaping. */
export function datasetToCsv(ds: TopicDataset): string {
  const escape = (cell: string | number) => {
    const s = String(cell);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  return ds.rows.map((row) => row.map(escape).join(",")).join("\n");
}

// Ensure unique export for builds that didn't use buildX above:
void buildTitration;
void buildCalorimetry;